import React from 'react'

import { string, bool, shape, arrayOf } from 'prop-types'

import { useActionsData } from 'Pods/UnifiedNavigation/Hooks/useActionsData'

import { EMPTY_ARRAY, EMPTY_OBJECT } from 'Utils/stableDefaults'

import CartProgressBar from './Components/CartProgressBar/CartProgressBar'
import UserAction from './Components/UserAction/UserAction'
import UserMenu from './Components/UserMenu/UserMenu'
import { useCartWidget } from './Hooks/useCartWidget'
import { useMessagesWidget } from './Hooks/useMessagesWidget'
import { useNotificationsWidget } from './Hooks/useNotificationsWidget'

// Actions the redesigned bar surfaces; upload / boost-credits are no longer in the
// top navigation.
const VISIBLE_ACTION_IDS = ['cart-action', 'inbox-action', 'notifications-action']

function UserActions({ actionsLinks = EMPTY_ARRAY, actionsConfig = EMPTY_OBJECT, locale = 'en' }) {
  const { isCurrentUser = false, newRoyaltyUi = false, showCartAds = false } = actionsConfig
  const { cart, messages, notifications, fetchNextCartDataPage } = useActionsData(isCurrentUser)

  const {
    isLoaded: isCartWidgetLoaded,
    loadWidget: loadCartWidget,
    popoverComponent: cartPopover,
    shouldShowCartProgress,
    cartProgress,
  } = useCartWidget({
    cart,
    fetchNextCartDataPage,
    locale,
    showCartAds,
    isCurrentUser,
  })

  const {
    isLoaded: isMessagesWidgetLoaded,
    loadWidget: loadMessagesWidget,
    popoverComponent: messagesPopover,
  } = useMessagesWidget({ messages })

  const {
    isLoaded: isNotificationsWidgetLoaded,
    loadWidget: loadNotificationsWidget,
    popoverComponent: notificationsPopover,
    markAllAsRead,
    unreadCount: notificationsUnreadCount,
  } = useNotificationsWidget({ notifications })

  const countsMap = {
    'cart-action': cart.meta.size,
    'inbox-action': messages.totalUnreadMessages,
    'notifications-action': notificationsUnreadCount,
  }

  function handleWidgetLoad(widgetId, callback) {
    if (widgetId === 'cart-action') {
      loadCartWidget(callback)
    }

    if (widgetId === 'inbox-action') {
      loadMessagesWidget(callback)
    }

    if (widgetId === 'notifications-action') {
      loadNotificationsWidget(callback)
    }
  }

  const popoversMap = {
    'cart-action': {
      hasPopover: true,
      isLoaded: isCartWidgetLoaded,
      component: cartPopover,
    },
    'inbox-action': {
      hasPopover: true,
      isLoaded: isMessagesWidgetLoaded,
      component: messagesPopover,
    },
    'notifications-action': {
      hasPopover: true,
      isLoaded: isNotificationsWidgetLoaded,
      component: notificationsPopover,
    },
  }

  const onOpenMap = {
    'notifications-action': markAllAsRead,
  }

  const bottomElementsMap = {
    'cart-action': <CartProgressBar shouldShowCartProgress={shouldShowCartProgress} cartProgress={cartProgress} />,
  }

  // Redesign uses the outline glyphs; the shared filled 'cart' / 'message' types stay
  // for the legacy nav. Overrides the server-provided icon for the new nav actions.
  const iconsMap = {
    'cart-action': 'cart-outline',
    'inbox-action': 'message-outline',
    'notifications-action': 'notification-outline',
  }

  const visibleActions = actionsLinks.filter(({ id }) => VISIBLE_ACTION_IDS.includes(id))

  return (
    <div className="unified-nav__actions">
      <ul className="unified-nav__actions-list">
        {visibleActions.map(({ id, icon, isForUser }) => (
          <UserAction
            key={id}
            id={id}
            icon={iconsMap[id] || icon}
            isForUser={isForUser}
            isCurrentUser={isCurrentUser}
            count={countsMap[id]}
            hasPopover={popoversMap[id]?.hasPopover}
            isPopoverLoaded={popoversMap[id]?.isLoaded}
            popoverComponent={popoversMap[id]?.component}
            bottomElement={bottomElementsMap[id] || null}
            onWidgetLoad={popoversMap[id]?.hasPopover ? handleWidgetLoad : null}
            onOpen={onOpenMap[id] || null}
          />
        ))}
      </ul>

      <UserMenu isCurrentUser={isCurrentUser} locale={locale} newRoyaltyUi={newRoyaltyUi} />
    </div>
  )
}

UserActions.propTypes = {
  actionsLinks: arrayOf(shape({
    id: string,
    href: string,
    icon: string,
    isForUser: bool,
  })),
  actionsConfig: shape({
    isCurrentUser: bool,
    newRoyaltyUi: bool,
    showCartAds: bool,
  }),
  locale: string,
}

export default UserActions
